import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { OPEN_GRAPH_LOCALES, SUPPORTED_LANGUAGES } from '../../i18n/config';
import { localizePath, stripLanguage } from '../../i18n/languagePath';
import { useLanguage } from '../../i18n/useLanguage';

import { SITE_URL } from './siteUrl';

/**
 * Retire les métadonnées par défaut de index.html (data-seo-default) : elles ne servent qu'aux
 * robots sans JavaScript, et React 19 ne les remplace pas (il ajoute les siennes à côté).
 * Appelé une fois, avant le premier rendu.
 */
export const removeStaticSeoDefaults = () => {
    document.head.querySelectorAll('[data-seo-default]').forEach((element) => element.remove());
};

interface SeoProps {
    /** Titre propre à la page, sans le nom du site (ajouté par le modèle seo.titleTemplate). */
    title: string;
    description?: string;
    /** Pages sans intérêt pour les moteurs de recherche (404, callback de connexion...). */
    noIndex?: boolean;
}

/**
 * Métadonnées de la page courante. React 19 place automatiquement <title>, <meta> et <link>
 * dans le <head>, et les retire quand la page change : aucune librairie nécessaire.
 *
 * - canonical : l'URL de la page dans la langue affichée ;
 * - hreflang : la même page dans chaque langue, et x-default (sans préfixe) qui redirige
 *   vers la langue du visiteur ;
 * - Open Graph / Twitter : aperçu lors d'un partage.
 */
export default function Seo({ title, description, noIndex = false }: SeoProps) {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const { pathname } = useLocation();

    const path = stripLanguage(pathname);
    const canonicalUrl = `${SITE_URL}${localizePath(language, path)}`;
    const fullTitle = t('seo.titleTemplate', { title });
    const metaDescription = description ?? t('seo.defaultDescription');

    return (
        <>
            <title>{fullTitle}</title>
            <meta name="description" content={metaDescription} />
            {noIndex ? (
                // Page non indexable : pas d'URL canonique ni d'alternatives (signaux contradictoires).
                <meta name="robots" content="noindex" />
            ) : (
                <>
                    <link rel="canonical" href={canonicalUrl} />
                    {SUPPORTED_LANGUAGES.map((lng) => (
                        <link
                            key={lng}
                            rel="alternate"
                            hrefLang={lng}
                            href={`${SITE_URL}${localizePath(lng, path)}`}
                        />
                    ))}
                    <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${path}`} />
                </>
            )}

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={t('app.name')} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:locale" content={OPEN_GRAPH_LOCALES[language]} />
            {SUPPORTED_LANGUAGES.filter((lng) => lng !== language).map((lng) => (
                <meta key={lng} property="og:locale:alternate" content={OPEN_GRAPH_LOCALES[lng]} />
            ))}
            <meta name="twitter:card" content="summary" />
        </>
    );
}
